import { useThemeStore } from "@stores/theme.store";
import { Button, DatePicker, Form, Input, message, Select } from "antd";
import moment from "moment";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { PopupSuccess } from "@components/general/popup";

import { PermitService } from "@services/permit.service";
import { NoticeType } from "antd/es/message/interface";
import { SchoolService } from "@services/school.service";
import { Controller, useForm } from "react-hook-form";
const { Option } = Select;
// moment.locale("id");
export const PermitPage = () => {
  const { setHeader, setAppBar } = useThemeStore((state) => state);
  const { control } = useForm();

  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const [thisDate, setThisDate] = useState<string>("");
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [showMessageText, setShowMessageText] = useState<string>("");
  const [messageType, setMessageType] = useState<string>("");

  const [popupSuccess, setPopupSuccess] = useState<boolean>(false);
  const [listOpenDate, setListOpenDate] = useState<Date[]>([]);

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [isIOSWebView, setIsIOSWebView] = useState(false);

  const { data: dataNis } = useQuery({
    queryKey: [SchoolService.queries.GET_SCHOOL_STUDENT_BIO],
    queryFn: SchoolService.studentBio,
  });
  const nis = dataNis?.data?.nis ?? "";

  const { data: settings } = useQuery({
    queryKey: [PermitService.queries.GET_PERMIT_SETTING],
    queryFn: PermitService.setting,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: PermitService.request,
    onSuccess: (response) => {
      if (response.success) {
        // setPopupSuccess(true);
        setShowMessage(true);
        setMessageType("success");
        setShowMessageText(response.message);
        form.resetFields();
        setStartDate(null);
        onChangeMode("");
        // refetchPermitData();
      } else {
        setShowMessage(true);
        setMessageType("error");
        setShowMessageText(response.message);
      }
    },
    onError: (error: any) => {
      setShowMessage(true);
      setMessageType("error");
      setShowMessageText(error.message ?? "Internal Error");
    },
  });

  useEffect(() => {
    setAppBar({ title: "Request Izin", show: true, leading: "back" });
    setHeader({
      buttonBack: true,
      filter: false,
      title: "Request Izin",
    });
  }, [setHeader, setAppBar]);

  useEffect(() => {
    if (showMessage) {
      messageApi.open({
        type: messageType as NoticeType,
        content: <>{showMessageText}</>,
      });
    }
    setShowMessage(false);
  }, [showMessage, showMessageText]);

  useEffect(() => {
    const userAgent = navigator.userAgent;

    // Very specific: only iOS devices in WebView (not Safari browser)
    const isReallyIOSWebView =
      /iPad|iPhone|iPod/.test(userAgent) &&
      !userAgent.includes("Safari/") &&
      !userAgent.includes("Chrome/");

    setIsIOSWebView(isReallyIOSWebView);
  }, []);

  const onChangeDate = (date: Date | null) => {
    if (date != null) {
      const formattedDate = dayjs(date).format("YYYY-MM-DD");

      form.setFieldsValue({
        datePermit: formattedDate,
        dateBack: formattedDate,
      });
      setStartDate(date);
      // setThisDate(formattedDate);
    }
  };

  const onChangeMode = (value: string | undefined) => {
    const nameMode = value ?? "";

    const dates = (settings?.data ?? [])
      .filter((item) => item.mode === nameMode)
      .map((item) => {
        const date = moment(item.date).toDate();
        return date;
      });

    setListOpenDate(dates.filter((date): date is Date => date !== null));
    setStartDate(null);
    form.setFieldsValue({ dateBack: "", datePermit: "" });
  };

  return (
    <>
      {contextHolder}
      <div className="tw-px-4 tw-pb-20">
        <Form
          form={form}
          className=""
          autoComplete="off"
          onFinish={(data) => {
            const { mode, datePermit } = data;

            // base permit time: same day at 07:00:00
            const permitDate =
              mode === "Keluar Kota/Menginap"
                ? dayjs(datePermit)
                    .set("hour", 15)
                    .set("minute", 30)
                    .set("second", 0)
                : dayjs(datePermit)
                    .set("hour", 7)
                    .set("minute", 0)
                    .set("second", 0);

            // base back time: either same day or next day at 17:00:00
            const backDate =
              mode === "Keluar Kota/Menginap"
                ? permitDate
                    .add(1, "day")
                    .set("hour", 17)
                    .set("minute", 0)
                    .set("second", 0)
                : permitDate.set("hour", 17).set("minute", 0).set("second", 0);

            mutate({
              ...data,
              nis,
              datePermit: permitDate.format("YYYY-MM-DD HH:mm:ss"),
              dateBack: backDate.format("YYYY-MM-DD HH:mm:ss"),
            });
          }}
        >
          <div className="tw-bg-other-100 tw-rounded-lg card-body">
            <Form.Item
              label="Pilih Izin"
              name="mode"
              className="tw-mb-6"
              rules={[{ required: true, message: "Pilih izin terlebih dulu!" }]}
            >
              <Select
                className="tw-border-other-0 tw-text-base"
                placeholder="Pilih izin"
                allowClear
                options={[
                  { value: "Pulang", label: "Perizinan Pulang" },
                  { value: "Sekitar Kota", label: "Perizinan Sekitar Kota" },
                  {
                    value: "Keluar Kota/Menginap",
                    label: "Perizinan Keluar Kota/Menginap",
                  },
                ]}
                onChange={onChangeMode}
              ></Select>
            </Form.Item>

            <Form.Item
              label="Nama Penjemput"
              name="pickup"
              className="tw-mb-6"
              rules={[
                { required: true, message: "Nama penjemput harus diisi!" },
              ]}
            >
              <Input
                className="tw-border-other-0 hover:tw-border-primary-hg tw-shadow-none focus:tw-shadow-none tw-text-base"
                placeholder="Nama Penjemput"
              />
            </Form.Item>

            <Form.Item
              label="Tanggal Izin"
              name="datePermit"
              className="tw-mb-6"
              rules={[{ required: true, message: "Tanggal izin harus diisi!" }]}
            >
              <Controller
                name="datePermit"
                control={control}
                render={() => (
                  <>
                    {isIOSWebView ? (
                      // Date select dropdown for mobile (shows only allowed dates)
                      <Select
                        placeholder="Pilih tanggal izin"
                        value={
                          startDate
                            ? moment(startDate).format("YYYY-MM-DD")
                            : undefined
                        }
                        onChange={(value) => {
                          if (value) {
                            const date = new Date(value);
                            onChangeDate(date);
                          }
                        }}
                        className="bg-theme"
                      >
                        {listOpenDate
                          .filter((date) => {
                            const checkDate = moment(date);
                            const today = moment();
                            const maxDate = moment().add(3, "months");
                            return (
                              checkDate.isSameOrAfter(today, "day") &&
                              checkDate.isSameOrBefore(maxDate, "day")
                            );
                          })
                          .map((date, index) => (
                            <Option
                              key={index}
                              value={moment(date).format("YYYY-MM-DD")}
                            >
                              {moment(date).format("DD MMMM YYYY")}
                            </Option>
                          ))}
                      </Select>
                    ) : (
                      <DatePicker
                        className="tw-border-other-0 hover:tw-border-primary-hg tw-shadow-none focus:tw-shadow-none tw-text-base"
                        placeholder="Tanggal Izin"
                        minDate={dayjs()}
                        maxDate={dayjs().add(2, "months")}
                        format="YYYY-MM-DD"
                        disabledDate={(currentDate: Dayjs) =>
                          !listOpenDate.some((date: Date) =>
                            dayjs(date).isSame(currentDate, "day")
                          )
                        }
                        // showTime={{
                        //   format: "HH:mm",
                        // }}
                        onChange={onChangeDate}
                        // value={thisDate}
                        inputReadOnly={true}
                      />
                    )}
                  </>
                )}
              />
            </Form.Item>

            <Form.Item
              label="Alasan Izin"
              name="notes"
              className="tw-mb-6"
              rules={[{ required: true, message: "Alasan izin harus diisi!" }]}
            >
              <Input
                className="tw-border-other-0 hover:tw-border-primary-hg tw-shadow-none focus:tw-shadow-none tw-text-base"
                placeholder="Alasan Izin"
              />
            </Form.Item>
          </div>
          <div className="tw-mt-3 tw-bg-other-100 tw-rounded-lg card-body">
            <Button
              type="primary"
              htmlType="submit"
              loading={isPending}
              className="tw-h-9 tw-w-full bg-gradient focus:tw-shadow-none hover:tw-bg-other-100 hover:tw-text-primary-hg hover:tw-border-primary-hg"
            >
              {isPending ? "" : "Request Izin"}
            </Button>
          </div>
        </Form>
      </div>

      <PopupSuccess
        open={popupSuccess}
        title="Berhasil!"
        message="Berhasil membuat permintaan izin!"
        textConfirm="Tutup"
        onConfirm={() => setPopupSuccess(false)}
      />
    </>
  );
};
